/**
 * Distance-based detail tiers for procedural AMHS kits.
 *
 * 0–15 m   → high      (full geometry, glass, internal robot)
 * 15–40 m  → medium    (shell + ports, simplified internals)
 * 40–100 m → low       (compact shell, block ports)
 * 100 m+   → very-low  (single instanced silhouette)
 */
export type AmhsDetailLevel = 'high' | 'medium' | 'low' | 'very-low';

export const AMHS_DETAIL_DIST_M = {
  HIGH_MAX: 25,
  MEDIUM_MAX: 55,
  LOW_MAX: 120,
} as const;

/** Throttle camera-distance LOD checks — avoids React churn while orbiting. */
export const AMHS_DETAIL_CHECK_INTERVAL_S = 0.25;

export function resolveAmhsDetailFromDistance(distanceM: number): AmhsDetailLevel {
  if (distanceM < AMHS_DETAIL_DIST_M.HIGH_MAX) return 'high';
  if (distanceM < AMHS_DETAIL_DIST_M.MEDIUM_MAX) return 'medium';
  if (distanceM < AMHS_DETAIL_DIST_M.LOW_MAX) return 'low';
  return 'very-low';
}

/** Smoked / transmission glass at HIGH and MEDIUM range. */
export function shouldRenderAmhsGlass(detail: AmhsDetailLevel): boolean {
  return detail === 'high' || detail === 'medium';
}

/** Full MeshPhysicalMaterial glass (transmission) — close and mid range. */
export function shouldRenderAmhsPhysicalGlass(detail: AmhsDetailLevel): boolean {
  return detail === 'high' || detail === 'medium';
}

/** Html HMI overlays only when the machine is close enough to read. */
export function shouldRenderAmhsHtmlOverlay(detail: AmhsDetailLevel): boolean {
  return detail === 'high';
}

export function pickEffectiveDetail(
  explicit: AmhsDetailLevel | undefined,
  auto: AmhsDetailLevel | undefined,
  autoDetailEnabled: boolean
): AmhsDetailLevel {
  if (explicit !== undefined) return explicit;
  if (autoDetailEnabled && auto !== undefined) return auto;
  return 'high';
}

/** Horizontal distance from camera to a world anchor (fab floor scale). */
export function horizontalDistanceM(
  cameraX: number,
  cameraZ: number,
  worldX: number,
  worldZ: number
): number {
  return Math.hypot(cameraX - worldX, cameraZ - worldZ);
}
